import { z } from "zod";
import { FeatureKeySchema } from "./feature-key.schemas.js";
import type { FeatureKey } from "./feature-key.schemas.js";
import { ConfigScopeSchema } from "./config-scope.schemas.js";
import { BaseFeatureConfigSchema } from "./base-feature-config.schemas.js";
import type { FeatureConfigTypeMap } from "../types/feature-config-type-map.types.js";

const _featureConfigSourcesSchema = z.object({
  enabled: ConfigScopeSchema,
  config: z.record(z.string(), ConfigScopeSchema),
});

const _resolvedFeatureConfigUserViewSchema = z.object({
  featureKey: FeatureKeySchema,
  enabled: z.boolean(),
  config: BaseFeatureConfigSchema.catchall(z.json()),
  sources: _featureConfigSourcesSchema,
});
export const ResolvedFeatureConfigUserViewSchema =
  _resolvedFeatureConfigUserViewSchema.readonly();

const _resolvedFeatureConfigAdminViewSchema =
  _resolvedFeatureConfigUserViewSchema.extend({
    hasDbOverride: z.boolean(),
    // Cascade origin (Default/Global/Event) of the resolved value, distinct
    // from the request-context scope of the lookup — see PLA-705.
    effectiveOverrideScope: ConfigScopeSchema,
  });
export const ResolvedFeatureConfigAdminViewSchema =
  _resolvedFeatureConfigAdminViewSchema.readonly();

export enum ResolvedConfigType {
  USER = "user",
  ADMIN = "admin",
}

export type ResolvedFeatureConfigUserView = z.infer<
  typeof ResolvedFeatureConfigUserViewSchema
>;

export type ResolvedFeatureConfigAdminView = z.infer<
  typeof ResolvedFeatureConfigAdminViewSchema
>;

/**
 * Heterogeneous view: admin-tier when the caller has admin privileges,
 * user-tier otherwise. Admin is first in the union so admin-shaped payloads
 * match the richer schema and never get silently downcast to the leaner user
 * schema (which would otherwise accept them since the user schema has no
 * required admin-metadata fields).
 */
export const ResolvedFeatureConfigViewUnionSchema = z.union([
  ResolvedFeatureConfigAdminViewSchema,
  ResolvedFeatureConfigUserViewSchema,
]);

export type ResolvedFeatureConfigViewUnion = z.infer<
  typeof ResolvedFeatureConfigViewUnionSchema
>;

export type TypedResolvedFeatureConfigUserView<K extends FeatureKey> =
  ResolvedFeatureConfigUserView & {
    readonly featureKey: K;
    readonly config: FeatureConfigTypeMap[K];
  };

export type TypedResolvedFeatureConfigAdminView<K extends FeatureKey> =
  ResolvedFeatureConfigAdminView & {
    readonly featureKey: K;
    readonly config: FeatureConfigTypeMap[K];
  };

type AnyResolvedFeatureConfigView =
  | ResolvedFeatureConfigUserView
  | ResolvedFeatureConfigAdminView;

export function isFeatureConfig<K extends FeatureKey>(
  config: ResolvedFeatureConfigUserView,
  key: K,
): config is TypedResolvedFeatureConfigUserView<K>;
export function isFeatureConfig<K extends FeatureKey>(
  config: ResolvedFeatureConfigAdminView,
  key: K,
): config is TypedResolvedFeatureConfigAdminView<K>;
export function isFeatureConfig<K extends FeatureKey>(
  config: AnyResolvedFeatureConfigView,
  key: K,
): boolean {
  return config.featureKey === key;
}

export type ResolvedConfigResult<T extends ResolvedConfigType> =
  T extends ResolvedConfigType.ADMIN
    ? ResolvedFeatureConfigAdminView
    : ResolvedFeatureConfigUserView;
